import React from 'react';
import { Autocomplete, TextField, Stack } from '@mui/material';
import { GroupFormState, GroupType } from '.';
import { sanitizeGroupName } from './util';
import { capitalizeWords } from '../../../../utils/common';

type Props = {
  formState: GroupFormState;
  setFormState: React.Dispatch<React.SetStateAction<GroupFormState>>;
  parentGroupOptions: [];
};
const CreateGroupForm: React.FC<Props> = ({ formState, setFormState, parentGroupOptions }) => {
  type HandleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => void;

  const handleGroupNameChange: HandleGroupNameChange = (e) => {
    const raw = e.target.value;
    const trimmed = raw.trimStart();

    const capitalized = capitalizeWords(trimmed);

    const { value, isEmpty, rejected } = sanitizeGroupName(capitalized);

    if (rejected) return;

    setFormState((prev: GroupFormState) => {
      if (isEmpty) {
        return {
          ...prev,
          groupName: '',
          errorStatus: { ...prev.errorStatus, groupName: true },
          errorFields: { ...prev.errorFields, groupName: 'Group name is required' },
        };
      }

      return {
        ...prev,
        groupName: value,
        errorStatus: { ...prev.errorStatus, groupName: false },
        errorFields: { ...prev.errorFields, groupName: '' },
      };
    });
  };

  const handleGroupTypeChange = (_event: React.SyntheticEvent, newValue: GroupType | null) => {
    setFormState((prev: GroupFormState) => ({
      ...prev,
      groupType: newValue,
      errorStatus: {
        ...prev.errorStatus,
        groupType: !newValue,
      },
      errorFields: {
        ...prev.errorFields,
        groupType: newValue ? '' : 'Please select a group',
      },
    }));
  };

  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        fullWidth
        label="Group Name"
        value={formState.groupName}
        onChange={handleGroupNameChange}
        error={formState.errorStatus.groupName}
        helperText={formState.errorFields.groupName}
        InputLabelProps={{ shrink: true }}
      />

      <Autocomplete<GroupType>
        options={parentGroupOptions}
        value={formState.groupType}
        onChange={handleGroupTypeChange}
        isOptionEqualToValue={(opt, val) => !!val && opt.value === val.value}
        getOptionLabel={(opt) => opt.label}
        componentsProps={{
          popper: {
            sx: { zIndex: 5600 },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Group"
            variant="outlined"
            fullWidth
            error={formState.errorStatus.groupType}
            helperText={formState.errorFields.groupType}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        )}
      />
    </Stack>
  );
};

export default CreateGroupForm;
